import {
  arrayOf,
  boolOf,
  fail,
  idOf,
  numberOf,
  objectOf,
  optionalText,
  textOf,
} from '../../../shared/api/wire'
import { decodeAttention } from '../../sale/api/contracts'
import type { FiscalState, PaymentKind, PaymentState } from '../../sale/model/types'
import type { AqsiRefund, AqsiRefundLine, SeatActive } from '../model/types'

const PAYMENT_KINDS: readonly PaymentKind[] = ['Cash', 'Card']
const PAYMENT_STATES: readonly PaymentState[] = ['NotRequired', 'Pending', 'InProgress', 'Succeeded', 'Failed', 'Unknown']
const FISCAL_STATES: readonly FiscalState[] = ['Pending', 'InProgress', 'Succeeded', 'Failed', 'Unknown']

function oneOf<T extends string>(value: unknown, allowed: readonly T[], field: string): T {
  const text = textOf(value, field)
  return (allowed as readonly string[]).includes(text) ? (text as T) : fail(`${field}: неизвестное значение ${text}`)
}

/** Копейки — целое: дробные «копейки» значат ответ другой схемы. */
function kopecksOf(value: unknown, field: string): number {
  const number = numberOf(value, field)
  return Number.isSafeInteger(number) ? number : fail(`${field} — не целые копейки`)
}

/** Количество `numeric(18,3)`: клиент держит его строкой для показа и не считает с ним. */
function quantityOf(value: unknown, field: string): string {
  if (typeof value === 'string' && /^\d+(\.\d{1,3})?$/.test(value)) return value
  const number = numberOf(value, field)
  const text = String(number)
  return number > 0 && /^\d+(\.\d{1,3})?$/.test(text) ? text : fail(`${field} — не количество`)
}

function decodeLine(raw: unknown, field: string): AqsiRefundLine {
  const item = objectOf(raw, field)
  return {
    id: idOf(item.id, `${field}.id`),
    ordinal: numberOf(item.ordinal, `${field}.ordinal`),
    name: textOf(item.name, `${field}.name`),
    quantity: quantityOf(item.quantity, `${field}.quantity`),
    priceKopecks: kopecksOf(item.priceKopecks, `${field}.priceKopecks`),
    amountKopecks: kopecksOf(item.amountKopecks, `${field}.amountKopecks`),
  }
}

/**
 * Возврат `aqsi.sale` вида `Refund` (схема `sale` draft.2 + C4b). Продажа тем же
 * видом конверта здесь отвергается: окно возврата не покажет продажу как возврат.
 */
export function decodeRefund(raw: unknown, field = 'возврат'): AqsiRefund {
  const data = objectOf(raw, field)
  if (data.kind !== 'Refund') fail(`${field}.kind: ожидался возврат`)
  return {
    id: idOf(data.id, `${field}.id`),
    sourceReceiptId: idOf(data.sourceReceiptId, `${field}.sourceReceiptId`),
    createdAt: textOf(data.createdAt, `${field}.createdAt`),
    paymentKind: oneOf(data.paymentKind, PAYMENT_KINDS, `${field}.paymentKind`),
    totalKopecks: kopecksOf(data.totalKopecks, `${field}.totalKopecks`),
    payment: oneOf(data.payment, PAYMENT_STATES, `${field}.payment`),
    fiscal: oneOf(data.fiscal, FISCAL_STATES, `${field}.fiscal`),
    purchaseOperationId: optionalText(data.purchaseOperationId, `${field}.purchaseOperationId`),
    slipId: optionalText(data.slipId, `${field}.slipId`),
    receiptOperationId: optionalText(data.receiptOperationId, `${field}.receiptOperationId`),
    receiptId: optionalText(data.receiptId, `${field}.receiptId`),
    lastMessage: optionalText(data.lastMessage, `${field}.lastMessage`),
    isFinished: boolOf(data.isFinished, `${field}.isFinished`),
    lines: arrayOf(data.lines, `${field}.lines`).map((item, index) => decodeLine(item, `${field}.lines[${index}]`)),
    attention: decodeAttention(data.attention, `${field}.attention`),
    version: numberOf(data.version, `${field}.version`),
  }
}

/**
 * `GET sales/active`: `null` — на кассе места ничего незавершённого; продажа —
 * только её id (окно возврата её не ведёт); возврат — целиком.
 */
export function decodeSeatActive(raw: unknown): SeatActive {
  if (raw === null || raw === undefined) return { kind: 'none' }
  const data = objectOf(raw, 'продажа кассы')
  if (data.kind === 'Refund') return { kind: 'refund', refund: decodeRefund(raw, 'возврат кассы') }
  if (data.kind === undefined || data.kind === 'Sale') return { kind: 'sale', saleId: idOf(data.id, 'продажа кассы.id') }
  // Возврат по слипу без чека (RP-FB74) касса держит, окно возврата по чеку его не ведёт: как чужая операция на кассе.
  if (data.kind === 'SlipRefund') return { kind: 'sale', saleId: idOf(data.id, 'продажа кассы.id') }
  return fail(`продажа кассы.kind: неизвестное значение ${typeof data.kind === 'string' ? data.kind : typeof data.kind}`)
}
